import { z } from 'zod';
import { PRODUCT_SIZES } from '../enums.js';
import { money } from './common.js';
/**
 * Каталог для клиента.
 *
 * Сервер отдаёт УЖЕ локализованные `name` и `description`, а не пару
 * `name_ru`/`name_uz`. Иначе каждый фронтенд повторяет логику выбора языка,
 * и они расходятся — а уведомления бота всё равно локализуются на сервере.
 */
export const publicProduct = z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    size: z.enum(PRODUCT_SIZES).nullable(),
    price: money,
    weight_min_kg: z.string().nullable(),
    weight_max_kg: z.string().nullable(),
    image_url: z.string().nullable(),
});
export const publicCategory = z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    name: z.string(),
    products: z.array(publicProduct),
});
export const catalogResponse = z.object({
    categories: z.array(publicCategory),
});
/**
 * Тип товара — то, что исполнитель выбирает вместо ввода названия: «Арбуз»
 * или «Дыня». За типом стоит категория каталога, и название товара сервер
 * берёт из неё же, поэтому вводить его руками не нужно.
 */
export const productType = z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    name: z.string(),
});
// ── Админские контракты ─────────────────────────────────────────────────────
const slug = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9-]+$/, 'Только строчные латинские буквы, цифры и дефис');
export const adminCategory = z.object({
    id: z.number().int().positive(),
    slug: z.string(),
    name_ru: z.string(),
    name_uz: z.string(),
    sort_order: z.number().int(),
    is_active: z.boolean(),
    products_count: z.number().int(),
});
export const createCategoryBody = z.object({
    slug,
    name_ru: z.string().min(1).max(120),
    name_uz: z.string().min(1).max(120),
    sort_order: z.number().int().min(0).max(9999).default(0),
    is_active: z.boolean().default(true),
});
export const updateCategoryBody = createCategoryBody.partial();
export const adminProduct = z.object({
    id: z.number().int().positive(),
    vendor_id: z.number().int().positive(),
    category_id: z.number().int().positive(),
    slug: z.string(),
    name_ru: z.string(),
    name_uz: z.string(),
    description_ru: z.string().nullable(),
    description_uz: z.string().nullable(),
    size: z.enum(PRODUCT_SIZES).nullable(),
    price: money,
    weight_min_kg: z.string().nullable(),
    weight_max_kg: z.string().nullable(),
    image_url: z.string().nullable(),
    is_available: z.boolean(),
    sort_order: z.number().int(),
});
export const createProductBody = z
    .object({
    /**
     * Витрина-владелец. Обязательна: товара «без продавца» в маркетплейсе не
     * существует, и колонка в базе объявлена NOT NULL. Админ выбирает витрину
     * в списке; исполнитель создаёт товар своим эндпоинтом, где витрина берётся
     * из сессии и подменить её нельзя.
     */
    vendor_id: z.number().int().positive(),
    category_id: z.number().int().positive(),
    slug,
    name_ru: z.string().min(1).max(200),
    name_uz: z.string().min(1).max(200),
    description_ru: z.string().max(2000).nullable().optional(),
    description_uz: z.string().max(2000).nullable().optional(),
    size: z.enum(PRODUCT_SIZES).nullable().optional(),
    // Цена строкой: numeric(12,2) в JSON-числе теряет точность на копейках
    price: money,
    weight_min_kg: money.nullable().optional(),
    weight_max_kg: money.nullable().optional(),
    is_available: z.boolean().default(true),
    sort_order: z.number().int().min(0).max(9999).default(0),
})
    .refine((v) => !v.weight_min_kg || !v.weight_max_kg || Number(v.weight_min_kg) <= Number(v.weight_max_kg), { message: 'Минимальный вес больше максимального', path: ['weight_min_kg'] });
export const updateProductBody = z.object({
    category_id: z.number().int().positive().optional(),
    slug: slug.optional(),
    name_ru: z.string().min(1).max(200).optional(),
    name_uz: z.string().min(1).max(200).optional(),
    description_ru: z.string().max(2000).nullable().optional(),
    description_uz: z.string().max(2000).nullable().optional(),
    size: z.enum(PRODUCT_SIZES).nullable().optional(),
    price: money.optional(),
    weight_min_kg: money.nullable().optional(),
    weight_max_kg: money.nullable().optional(),
    is_available: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(9999).optional(),
});
export const adminProductsQuery = z.object({
    vendor_id: z.coerce.number().int().positive().optional(),
});
export const uploadedImage = z.object({ image_url: z.string() });
//# sourceMappingURL=catalog.js.map